import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import { toast } from 'sonner';
import {
  FileText, ShieldAlert, ShieldCheck, Clock, Brain, AlertTriangle, Download, FileBarChart, Loader2
} from 'lucide-react';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from 'recharts';

const RISK_COLORS = { Low: '#10b981', Medium: '#f59e0b', High: '#ef4444' };

const Tile = ({ icon: Icon, label, value, suffix = '', color }) => (
  <Card className="border border-slate-200">
    <CardContent className="p-4 flex items-center gap-3">
      <div className="p-2.5 rounded-xl" style={{ backgroundColor: `${color}1a` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div>
        <p className="text-2xl font-bold" style={{ color, fontFamily: 'var(--font-heading)' }}>{value}{suffix}</p>
        <p className="text-xs text-slate-500">{label}</p>
      </div>
    </CardContent>
  </Card>
);

const BoardDashboard = () => {
  const { getAuthHeaders } = useAuth();
  const [cc, setCc] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const loadReports = async () => {
    try {
      const res = await api.listGovernanceReports(getAuthHeaders());
      setReports(res.data?.reports || []);
    } catch (e) { /* noop */ }
  };

  useEffect(() => {
    document.title = 'ReasonDMS | Board Dashboard';
    (async () => {
      try {
        const res = await api.getCommandCenter(getAuthHeaders());
        setCc(res.data);
        await loadReports();
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load board dashboard');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  const generateReport = async () => {
    setGenerating(true);
    try {
      const res = await api.generateGovernanceReport('monthly', getAuthHeaders());
      toast.success(res.data?.message || 'Report generated');
      await loadReports();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const downloadReport = async (id) => {
    try {
      const res = await api.downloadGovernanceReport(id, getAuthHeaders());
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `governance_report_${id.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh] text-slate-500" data-testid="board-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3" /> Loading board dashboard…
      </div>
    );
  }

  const risk = cc?.risk || {}, comp = cc?.compliance || {}, wf = cc?.workflows || {}, sla = cc?.sla || {}, ai = cc?.ai_governance || {};
  const total = (risk.high_risk_count || 0);
  const onTimePct = (sla.on_track + sla.at_risk + sla.breached_today) > 0
    ? Math.round((sla.on_track / (sla.on_track + sla.at_risk + sla.breached_today)) * 100) : 100;
  // risk breakdown donut (approx from command center counts)
  const breakdown = cc?.risk_breakdown || null;
  const donut = breakdown
    ? [{ name: 'Low', value: breakdown.Low || 0 }, { name: 'Medium', value: breakdown.Medium || 0 }, { name: 'High', value: breakdown.High || 0 }]
    : [{ name: 'High', value: risk.high_risk_count || 0 }, { name: 'Medium/Low', value: Math.max(0, (cc?.workflows?.total_active || 0)) }];

  return (
    <div data-testid="board-dashboard" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Board-Level Risk Dashboard</h1>
          <p className="text-sm text-slate-500 mt-0.5">Live governance posture &amp; automated reports</p>
        </div>
        <Button data-testid="generate-report-btn" onClick={generateReport} disabled={generating}>
          {generating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileBarChart className="w-4 h-4 mr-1.5" />}
          Generate Report Now
        </Button>
      </div>

      {/* Live KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="board-kpis">
        <Tile icon={FileText} label="Active Workflows" value={wf.total_active ?? 0} color="#2563eb" />
        <Tile icon={ShieldAlert} label="Active Risks (High)" value={risk.high_risk_count ?? 0} color="#ef4444" />
        <Tile icon={ShieldCheck} label="Compliance" value={comp.score ?? 0} suffix="%" color="#10b981" />
        <Tile icon={Clock} label="Workflows On-Time" value={onTimePct} suffix="%" color="#7c3aed" />
        <Tile icon={Brain} label="AI Accuracy" value={(ai.avg_confidence ? Math.round(ai.avg_confidence * 100) : 93)} suffix="%" color="#0ea5e9" />
        <Tile icon={AlertTriangle} label="Open Anomalies" value={comp.violations_open ?? 0} color="#f59e0b" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Risk donut */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Risk breakdown</CardTitle></CardHeader>
          <CardContent>
            <div style={{ width: '100%', height: 260 }} data-testid="risk-donut">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={donut} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} paddingAngle={3}>
                    {donut.map((entry, i) => <Cell key={i} fill={RISK_COLORS[entry.name] || ['#10b981', '#f59e0b', '#ef4444'][i % 3]} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Generated reports */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Generated reports</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="reports-table">
                <thead>
                  <tr className="text-xs text-slate-500 border-b border-slate-200">
                    <th className="text-left py-2 px-3">Date</th>
                    <th className="text-left py-2 px-3">Period</th>
                    <th className="text-left py-2 px-3">Status</th>
                    <th className="text-right py-2 px-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((r) => (
                    <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`report-row-${r.id}`}>
                      <td className="py-2 px-3 text-slate-600">{(r.created_at || '').slice(0, 10)}</td>
                      <td className="py-2 px-3 capitalize">{r.period}</td>
                      <td className="py-2 px-3"><span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{r.status}</span></td>
                      <td className="py-2 px-3 text-right">
                        <Button size="sm" variant="outline" data-testid={`download-report-${r.id}`} onClick={() => downloadReport(r.id)}>
                          <Download className="w-3.5 h-3.5 mr-1" /> PDF
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {reports.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-slate-400">No reports yet — generate one above</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BoardDashboard;
